import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { IPatient } from '../../caremap/ICareMap';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { HeaderPopover } from '../../components/HeaderPopover';
import { Text } from '../../components/Text';
import { TabRadioButtons } from '../../components/TabRadioButtons';
import { InfoIcon } from '../../theme/Icons';
import { ViewShareLinkButton } from '../ViewShareLinkButton';
import { SHARING_ACTION_BUTTON_MIN_WIDTH } from '../sharingActionButton';
import { CreateCareCodeButton } from '../care-code/CreateCareCodeButton';
import { CARE_CODE_DESCRIPTION } from '../care-code/careCodeCopy';
import { SharingPeopleList } from './SharingPeopleList';

const useStyles = createUseStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14
    },
    divider: {
        height: 1,
        background: 'var(--ion-color-step-100)'
    },
    // Section title paired with its info tooltip trigger.
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4
    },
    actionButton: {
        minWidth: SHARING_ACTION_BUTTON_MIN_WIDTH
    },
    // ViewShareLinkButton renders its description as a default-size <p>; normalize it to
    // match the other section descriptions.
    shareLinkWrap: {
        '& p': {
            fontSize: '14px !important',
            color: 'var(--ion-color-step-750) !important',
            lineHeight: 1.45,
            margin: '10px 0 0 !important'
        },
        '& p strong': {
            color: 'var(--ion-text-color)'
        }
    },
    popoverRole: {
        fontWeight: 700,
        color: 'var(--ion-text-color)'
    },
    popoverItem: {
        marginTop: 10
    }
});

export interface ISharingAndAccessContentProps {
    // Opens a care map member's edit drawer; the sheet closes itself first, the page just goes.
    navigate: (path: string) => void;
}

// Everyone with access, temporary access, the care code and the default note visibility,
// rendered inline on the Community page and in the sheet other pages open.
export const SharingAndAccessContent: FC<ISharingAndAccessContentProps> = ({ navigate }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const caremap = careMapState.getActiveCaremap();
    const patient = caremap?.patient;

    if (!caremap || !patient) {
        return null;
    }

    const openMemberInCareMap = (memberId: string) => {
        const link = careMapState.maybeGetMemberLink(memberId);
        if (!link) {
            return;
        }
        const path = link.hiveKey === 'household'
            ? `/caremaps/${caremap.careMapId}/drawer/household/member/${memberId}/edit`
            : `/caremaps/${caremap.careMapId}/drawer/support-group/${link.hiveKey}/member/${memberId}/edit`;
        navigate(path);
    };

    return (
        <div className={classes.content} data-id="sharing-and-access-content">
            <PeopleWithAccess patient={patient} onUpdateInCareMap={openMemberInCareMap} />
            {/* The view-only link only works when the server granted the link (HATEOAS: link
                presence = permission), so the whole section is gated on it. */}
            {Boolean(caremap.links?.createViewerAccessToken) && <TemporaryAccess />}
            <div className={classes.divider} />
            <DefaultVisibility patient={patient} />
        </div>
    );
};

const PeopleWithAccess: FC<{ patient: IPatient; onUpdateInCareMap: (memberId: string) => void; }> = ({ patient, onUpdateInCareMap }) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);
    const community = communityFactory.forActiveCaremap();

    const [showAccessInfo, setShowAccessInfo] = useState(false);

    const loaded = community.isLoaded();
    const hasAnyone = loaded && (community.getPermanentCoOwners().length > 0 || community.getPermanentContributors().length > 0 || community.getPermanentViewers().length > 0 || community.getInvites('co-owner').length > 0 || community.getInvites('contributor').length > 0 || community.getInvites('view-only').length > 0);

    return (
        <>
            <div className={classes.sectionHeader}>
                <Text type="subtitle">People with access</Text>
                <IonButton id="access-info-trigger" fill="clear" color="medium" size="small" onClick={() => setShowAccessInfo(v => {
                    if (!v) {
                        mixpanel.track('Tooltip Opened', () => ({ Tooltip: 'Access Levels' }));
                    }
                    return !v;
                })} aria-label="About access levels"><InfoIcon /></IonButton>
            </div>
            {hasAnyone && (
                <Text type="detail">
                    Viewers see shared notes and recordings. Contributors see what viewers see and also add notes. Co-Owners have full access and can invite others.
                </Text>
            )}

            {!loaded || hasAnyone ? (
                <SharingPeopleList onUpdateInCareMap={onUpdateInCareMap} />
            ) : (
                <Text type="detail">
                    No one else has access yet. Invite others when you're ready to start collaborating!
                </Text>
            )}

            <AccessLevelsPopover patient={patient} isOpen={showAccessInfo} onDidDismiss={() => setShowAccessInfo(false)} />
        </>
    );
};

const AccessLevelsPopover: FC<{ patient: IPatient; isOpen: boolean; onDidDismiss: () => void; }> = ({ patient, isOpen, onDidDismiss }) => {
    const classes = useStyles();
    return (
        <HeaderPopover
            trigger="access-info-trigger"
            isOpen={isOpen}
            onDidDismiss={onDidDismiss}
            maxWidth="default"
            title="Sharing Options"
            content={
                <Text type="detail">
                    <div>
                        <span className={classes.popoverRole}>Viewer</span>: sees shared notes, recordings, and appointments, and can comment. Can't edit anything or see hidden info (hidden notes, recordings, medications, diagnoses).
                    </div>
                    <div className={classes.popoverItem}>
                        <span className={classes.popoverRole}>Contributor</span>: adds notes and recordings about <span className="mp-mask">{patient.firstName || 'the care recipient'}</span>, which they can edit, and sees what a viewer sees. For a paid aide, a neighbor or a friend. Free.
                    </div>
                    <div className={classes.popoverItem}>
                        <span className={classes.popoverRole}>Co-Owner</span>: a full partner. Sees everything, including hidden notes, recordings, medications, and diagnoses. Can add and edit notes, recordings, appointments, and tasks, and can invite and remove others. Two included in free.
                    </div>
                    <div className={classes.popoverItem}>
                        <span className={classes.popoverRole}>Share link</span>: temporary access without needing an account. Sees shared notes, recordings, and appointments but can't comment.
                    </div>
                </Text>
            }
        />
    );
};

const TemporaryAccess: FC = () => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const [showCareCodeInfo, setShowCareCodeInfo] = useState(false);
    const [showShareLink, setShowShareLink] = useState(false);

    return (
        <>
            <Text type="subtitle">Share Temporary Access</Text>
            {showShareLink && (
                <div className={classes.shareLinkWrap}>
                    <ViewShareLinkButton trackingFrom="Sharing Set Up" />
                </div>
            )}
            <IonButton fill="outline" size="small" className={classes.actionButton} onClick={() => setShowShareLink(v => !v)}>
                {showShareLink ? 'Done' : '+ Create Link'}
            </IonButton>

            <div className={classes.sectionHeader}>
                <Text type="subtitle">Printable Care Code</Text>
                <IonButton id="care-code-info-trigger" fill="clear" color="medium" size="small" onClick={() => setShowCareCodeInfo(v => {
                    if (!v) {
                        mixpanel.track('Tooltip Opened', () => ({ Tooltip: 'Care Code' }));
                    }
                    return !v;
                })} aria-label="About the Care Code"><InfoIcon /></IonButton>
            </div>
            <CreateCareCodeButton trackingFrom="Sharing Set Up" />

            <HeaderPopover
                trigger="care-code-info-trigger"
                isOpen={showCareCodeInfo}
                onDidDismiss={() => setShowCareCodeInfo(false)}
                maxWidth="default"
                title="Printable Care Code"
                content={
                    <Text type="detail">{CARE_CODE_DESCRIPTION}</Text>
                }
            />
        </>
    );
};

const DefaultVisibility: FC<{ patient: IPatient; }> = ({ patient }) => {
    const careMapState = useInstance(CareMapState);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const isShared = patient.notesIsPublic === true;

    const updateVisibilityDefault = (shared: boolean) => {
        if (shared !== isShared) {
            mixpanel.track('Default Note Visibility Changed', () => ({ 'State': shared ? 'shared' : 'private' }));
            careMapState.updatePatient({ ...patient, notesIsPublic: shared });
        }
    };

    return (
        <>
            <Text type="subtitle">Default Note & Recording Access for Viewers</Text>
            <TabRadioButtons
                value={isShared}
                onChange={updateVisibilityDefault}
                buttons={[
                    { label: 'Shared', value: true },
                    { label: 'Hidden', value: false }
                ]}
            />
            <Text type="detail">
                {isShared
                    ? 'New notes and recordings are visible to you, viewers, and co-owners. You can still hide any individual note from viewers.'
                    : 'New notes and recordings stay between you and any co-owners. You choose what to share with viewers.'}
            </Text>
        </>
    );
};
